import {
    Box,
    Button,
    capitalize,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {BaustellenInfo} from "../../api";
import DataTable from "../data/DataTable";
import {formatAsMoney} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";

export type BaustellenFilter = {
    search?: string;
    archivierte?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<BaustellenFilter>, "filter"> &
    Partial<Pick<DataRequest<BaustellenFilter>, "filter">>;

const tableName: string = "baustellen"

export default function BaustellenDataTable({...input}: Props) {
    const request = useDataRequest<BaustellenFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <BaustellenResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<BaustellenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<BaustellenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            archivierte: "nur aktive",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            archivierte: currentCustomFilter.archivierte,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <FormControl size="small" sx={{width: "150px", ml: 1}}>
                        <InputLabel>Archiv</InputLabel>
                        <Select
                            label="Belegstatus"
                            size="small"
                            value={currentCustomFilter.archivierte ?? "nur aktive"}
                            onChange={(val) => setCustomField("archivierte", val.target.value)}
                        >
                            <MenuItem key="nur aktive" value="nur aktive">
                                Nur aktive
                            </MenuItem>
                            <MenuItem key="alle" value="alle">
                                Alle
                            </MenuItem>
                            <MenuItem key="nur archivierte" value="nur archivierte">
                                Nur archivierte
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-baustelle");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <FormControl size="small" sx={{width: "150px", ml: 1}}>
                        <InputLabel>Archiv</InputLabel>
                        <Select
                            label="Belegstatus"
                            size="small"
                            value={currentCustomFilter.archivierte ?? "nur aktive"}
                            onChange={(val) => setCustomField("archivierte", val.target.value)}
                        >
                            <MenuItem key="nur aktive" value="nur aktive">
                                Nur aktive
                            </MenuItem>
                            <MenuItem key="alle" value="alle">
                                Alle
                            </MenuItem>
                            <MenuItem key="nur archivierte" value="nur archivierte">
                                Nur archivierte
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-baustelle");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function BaustellenResults({
                               request,
                               setFilteredState,
                           }: {
    request: DataRequestState<BaustellenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<BaustellenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadBaustellen: DataLoader<BaustellenFilter, BaustellenInfo> =
        useCallback(
            async (params) =>
                (await getApi()).baustellen.sucheBaustellen({...params}),
            [getApi]
        );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadBaustellen}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open(`/baustellen/${row.id}`, "_blank");
                } else {
                    navigate(`/baustellen/${row.id}`);
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<BaustellenInfo>> {
    const {t} = useT("customer");

    return useMemo(
        () => [
            {
                field: "nummer",
                headerName: t("number"),
                type: "number",
                renderCell: (params) => {
                    const value = params.value as number | null | undefined;
                    return value !== null && value !== undefined ? `${value}` : "";
                },
            },
            {
                field: "name",
                headerName: t("name"),
                flex: 1,
            },
            {
                field: "kundenName",
                headerName: t("customer-name"),
                flex: 1,
            },
            {
                field: "strasse",
                headerName: t("street"),
                flex: 1,
            },
            {
                field: "plz",
                headerName: t("plz"),
                flex: 1,
            },
            {
                field: "ort",
                headerName: t("city"),
                flex: 1,
            },
            {
                field: "auftragsSumme",
                headerName: t("order-total"),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "saldo",
                headerName: t("balance"),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
        ],
        [t]
    );
}
